import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, studentPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StateSelect } from "@/components/StateSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { CC_ROLES, ROTATION_LEVELS } from "@/lib/constants";
import { loadOptionList, optionLabels } from "@/lib/options";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { DeleteButton } from "@/components/DeleteButton";
import { AwaitingDecisions } from "@/components/AwaitingDecisions";
import { fmtDate } from "@/lib/dates";

const DISCIPLINES = ["PT", "PTA", "OT", "OTA", "SLP", "Other"];

export default function Placements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [schools, setSchools] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [rotationLevels, setRotationLevels] = useState(ROTATION_LEVELS);
  const [form, setForm] = useState({ discipline: "PT", rotation_level: ROTATION_LEVELS[1], rotation_type: "Full-time", request_date: new Date().toISOString().slice(0, 10), student_unknown: false });
  const [saving, setSaving] = useState(false);
  const [allowedStates, setAllowedStates] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [q, setQ] = useState("");
  const [fDisc, setFDisc] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fClear, setFClear] = useState("all");

  useEffect(() => {
    if (!form.preferred_location_state) { setFacilities([]); return; }
    api.get("/facilities/for-request", { params: { state: form.preferred_location_state } })
      .then(({ data }) => setFacilities(data)).catch(() => setFacilities([]));
  }, [form.preferred_location_state]);

  const canCreate = ["Super Admin", "Campus Coordinator", "School User"].includes(user.role);

  const statuses = [...new Set((rows || []).map((r) => r.placement_status).filter(Boolean))].sort();
  const clearances = [...new Set((rows || []).map((r) => r.clearance_status).filter(Boolean))].sort();
  const ql = q.trim().toLowerCase();
  const filtered = (rows || []).filter((r) => {
    if (fDisc !== "all" && r.discipline !== fDisc) return false;
    if (fStatus !== "all" && r.placement_status !== fStatus) return false;
    if (fClear !== "all" && r.clearance_status !== fClear) return false;
    if (ql) {
      const hay = [r.student?.name, r.school?.name, r.facility?.name, r.discipline, r.placement_status, r.clearance_status].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(ql)) return false;
    }
    return true;
  });

  const load = () => api.get("/placement-requests").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    loadOptionList("rotation_levels").then((opts) => { if (opts.length) setRotationLevels(optionLabels(opts)); }).catch(() => {});
    if (canCreate) {
      api.get("/schools").then(({ data }) => {
        setSchools(data);
        // Smart default: pre-fill the rotation state from the school's home state (still editable for out-of-state rotations).
        if (user.role === "School User") {
          const mySchool = data.find((s) => s.id === user.organization_id);
          if (mySchool?.state) setForm((f) => (f.preferred_location_state ? f : { ...f, preferred_location_state: mySchool.state }));
        }
      }).catch(() => {});
      api.get("/placement-states").then(({ data }) => setAllowedStates(data.states || [])).catch(() => {});
    }
    if (user.role === "School User") {
      setForm((f) => ({ ...f, clinical_educator_name: user.name, clinical_educator_email: user.email }));
    }
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const school_id = user.role === "School User" ? user.organization_id : form.school_id;
      const payload = { ...form, school_id };
      if (form.student_unknown) payload.student_name = "UNKNOWN";
      await api.post("/placement-requests", payload);
      toast.success("Placement request submitted");
      setOpen(false);
      setForm({ discipline: "PT", rotation_level: ROTATION_LEVELS[1], rotation_type: "Full-time", request_date: new Date().toISOString().slice(0, 10), student_unknown: false });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Placement Requests" subtitle="The central pipeline of student intern placements">
        <div className="flex items-center gap-2">
          {canCreate && user.role !== "School User" && <Button variant="outline" onClick={() => navigate("/touchpoints")} data-testid="placements-touchpoints-btn">Student Touchpoints</Button>}
          {canCreate && <Button onClick={() => setOpen(true)} className="bg-cc-blue hover:bg-cc-blue-dark text-white" data-testid="new-request-btn">+ New Request</Button>}
        </div>
      </PageHeader>

      {CC_ROLES.includes(user.role) && <div className="mb-6"><AwaitingDecisions /></div>}
      <Card className="overflow-hidden">
        {!rows ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cc-blue" /></div> :          rows.length === 0 ? <EmptyState text="No placement requests yet." /> : (
            <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search student, school, facility…" className="max-w-xs" data-testid="pr-search" />
              <Select value={fDisc} onValueChange={setFDisc}><SelectTrigger className="w-36" data-testid="pr-filter-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger><SelectContent><SelectItem value="all">All disciplines</SelectItem>{DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
              <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="w-52" data-testid="pr-filter-status"><SelectValue placeholder="Placement status" /></SelectTrigger><SelectContent><SelectItem value="all">All placement statuses</SelectItem>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Select value={fClear} onValueChange={setFClear}><SelectTrigger className="w-48" data-testid="pr-filter-clearance"><SelectValue placeholder="Clearance" /></SelectTrigger><SelectContent><SelectItem value="all">All clearance</SelectItem>{clearances.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              {(q || fDisc !== "all" || fStatus !== "all" || fClear !== "all") && <Button variant="ghost" size="sm" onClick={() => { setQ(""); setFDisc("all"); setFStatus("all"); setFClear("all"); }} data-testid="pr-clear-filters">Clear</Button>}
              <span className="text-xs text-zinc-400 ml-auto" data-testid="pr-count">{filtered.length} of {rows.length}</span>
            </div>
            <div className="overflow-x-auto cc-scroll -mx-5 -mb-5">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-3">Student</th>
                    <th className="text-left px-5 py-3">School</th>
                    <th className="text-left px-5 py-3">Discipline</th>
                    <th className="text-left px-5 py-3">Facility</th>
                    <th className="text-left px-5 py-3">Placement</th>
                    <th className="text-left px-5 py-3">Clearance</th>
                    <th className="text-left px-5 py-3">Start</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-zinc-400">No requests match your filters.</td></tr>}
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-zinc-50 cursor-pointer" onClick={() => navigate(`/placements/${r.id}`)} data-testid={`pr-row-${r.id}`}>
                      <td className="px-5 py-3 font-medium text-zinc-800"><div className="flex items-center gap-2.5"><Avatar src={studentPhoto(r.student_id)} name={r.student?.name} size={30} testId={`pr-avatar-${r.id}`} />{r.student?.name}</div></td>
                      <td className="px-5 py-3 text-zinc-600">{r.school?.name}</td>
                      <td className="px-5 py-3"><span className="font-mono text-xs px-2 py-0.5 bg-zinc-100 rounded">{r.discipline}</span></td>
                      <td className="px-5 py-3 text-zinc-600">{r.facility?.name || "—"}</td>
                      <td className="px-5 py-3"><StatusBadge status={r.placement_status} /></td>
                      <td className="px-5 py-3"><StatusBadge status={r.clearance_status} /></td>
                      <td className="px-5 py-3 text-zinc-600 font-mono text-xs">{fmtDate(r.accepted_start_date || r.requested_start_date)}</td>
                      <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" className="text-cc-blue" onClick={() => navigate(`/placements/${r.id}`)}>Open</Button>
                        {CC_ROLES.includes(user.role) && <DeleteButton endpoint={`/placement-requests/${r.id}`} label="placement request" name={r.student?.name} onDeleted={load} testId={`delete-pr-${r.id}`} className="ml-2" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto cc-scroll">
          <DialogHeader><DialogTitle>Student Placement Request</DialogTitle></DialogHeader>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="request-disclaimer">
            <p>All requests are subject to verification of an active affiliation agreement between TMC and the School.</p>
          </div>

          <div className="space-y-4 mt-2">
            <p className="text-xs text-zinc-500">Fields marked with an asterisk (<span className="text-red-500">*</span>) are required.</p>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Request date *"><Input type="date" data-testid="form-request-date" value={form.request_date || ""} onChange={(e) => setForm({ ...form, request_date: e.target.value })} /></Field>
              {user.role !== "School User" ? (
                <Field label="College or University *">
                  <Select value={form.school_id} onValueChange={(v) => {
                    const sch = schools.find((s) => s.id === v);
                    setForm((f) => ({ ...f, school_id: v, ...(sch?.state && !f.preferred_location_state ? { preferred_location_state: sch.state } : {}) }));
                  }}>
                    <SelectTrigger data-testid="form-school"><SelectValue placeholder="Select school" /></SelectTrigger>
                    <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              ) : <div />}
              <Field label="Campus location — City"><Input data-testid="form-campus-city" value={form.campus_city || ""} onChange={(e) => setForm({ ...form, campus_city: e.target.value })} /></Field>
            </div>

            <div className="border-t pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Clinical Educator / Fieldwork Coordinator</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *"><Input data-testid="form-educator-name" value={form.clinical_educator_name || ""} onChange={(e) => setForm({ ...form, clinical_educator_name: e.target.value })} /></Field>
                <Field label="Email *"><Input data-testid="form-educator-email" value={form.clinical_educator_email || ""} onChange={(e) => setForm({ ...form, clinical_educator_email: e.target.value })} /></Field>
              </div>
            </div>

            <div className="border-t pt-3">
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <Checkbox data-testid="form-student-unknown" checked={form.student_unknown} onCheckedChange={(v) => setForm({ ...form, student_unknown: !!v })} />
                Student not yet known (use "UNKNOWN" for now)
              </label>
              {!form.student_unknown && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Student full name"><Input data-testid="form-student-name" value={form.student_name || ""} onChange={(e) => setForm({ ...form, student_name: e.target.value })} /></Field>
                  <Field label="Student personal email"><Input data-testid="form-student-personal-email" value={form.student_personal_email || ""} onChange={(e) => setForm({ ...form, student_personal_email: e.target.value })} /></Field>
                  <Field label="Student school email"><Input data-testid="form-student-email" value={form.student_school_email || ""} onChange={(e) => setForm({ ...form, student_school_email: e.target.value })} /></Field>
                  <Field label="Student cell number"><Input data-testid="form-student-cell" value={form.student_cell_phone || ""} onChange={(e) => setForm({ ...form, student_cell_phone: e.target.value })} /></Field>
                </div>
              )}
            </div>

            <div className="border-t pt-3 grid grid-cols-2 gap-3">
              <Field label="Discipline *">
                <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                  <SelectTrigger data-testid="form-discipline"><SelectValue /></SelectTrigger>
                  <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Graduation date *"><Input type="date" data-testid="form-grad-date" value={form.graduation_date || ""} onChange={(e) => setForm({ ...form, graduation_date: e.target.value })} /></Field>
              <Field label="Rotation level *">
                <Select value={form.rotation_level} onValueChange={(v) => setForm({ ...form, rotation_level: v })}>
                  <SelectTrigger data-testid="form-rotation-level"><SelectValue placeholder="Select level" /></SelectTrigger>
                  <SelectContent>{rotationLevels.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="State for rotation request *">
                <StateSelect value={form.preferred_location_state} onChange={(v) => setForm({ ...form, preferred_location_state: v, preferred_facility_id: undefined })} testId="form-preferred-state" allowed={allowedStates} placeholder="Select a state we serve…" />
                <p className="text-xs text-zinc-400 mt-1">Defaults to the school's state — change it for out-of-state rotations.</p>
              </Field>
              <Field label="Facility (choose the closest) *">
                <Select value={form.preferred_facility_id || ""} onValueChange={(v) => setForm({ ...form, preferred_facility_id: v })} disabled={!form.preferred_location_state}>
                  <SelectTrigger data-testid="form-facility"><SelectValue placeholder={form.preferred_location_state ? (facilities.length ? "Select a facility" : "No facilities with instructors in this state") : "Pick a state first"} /></SelectTrigger>
                  <SelectContent>{facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}{f.city ? ` — ${f.city}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Rotation start date *"><Input type="date" data-testid="form-start" value={form.requested_start_date || ""} onChange={(e) => setForm({ ...form, requested_start_date: e.target.value })} /></Field>
              <Field label="Rotation end date *"><Input type="date" data-testid="form-end" value={form.requested_end_date || ""} onChange={(e) => setForm({ ...form, requested_end_date: e.target.value })} /></Field>
            </div>

            <Field label="Clinical educator comments"><Textarea data-testid="form-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || (!form.student_unknown && !form.student_name) || !form.clinical_educator_name || !form.clinical_educator_email || !form.preferred_location_state || !form.preferred_facility_id || !form.requested_start_date} className="bg-cc-blue text-white" data-testid="submit-request">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
